/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/utils/element-plus-popover-close-fix.ts
 * 版本: v1.0
 * 创建日期: 2026-01-18
 * 创建人: AI Assistant
 *
 * Element Plus 下拉/弹层“点击空白不收起”统一修复
 *
 * 背景：
 * - 部分页面出现 ElSelect/ElCascader/ElDatePicker 等弹层展开后，
 *   点击空白区域无法收起、必须点回表单才收起的问题（体验很差）。
 *
 * 机制说明：
 * - Element Plus 的关闭依赖 click-outside/blur 等事件链路。
 * - 若某些布局/层级导致 click-outside 失效，用户点击空白不会触发关闭。
 *
 * 方案：
 * - 在 document 的捕获阶段监听 pointerdown；
 * - 若当前聚焦元素属于 Element Plus 的“会弹出 popper 的输入类组件”，
 *   且点击目标不在触发器/弹层内，则主动 blur 当前聚焦元素，
 *   触发 Element Plus 的关闭逻辑。
 */
export function installElementPlusPopoverCloseFix(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  // 避免重复安装（多次 mount/热更新时）
  const key = '__ms_el_popover_close_fix_installed__'
  if ((window as unknown as Record<string, unknown>)[key]) return
  ;(window as unknown as Record<string, unknown>)[key] = true

  const POPPER_SELECTOR = '.el-popper'
  const TRIGGER_SELECTOR = [
    '.el-select',
    '.el-cascader',
    '.el-date-editor',
    '.el-time-picker',
    '.el-autocomplete'
  ].join(',')
  const FORM_ITEM_SELECTOR = '.el-form-item'
  // label 区域在 Element Plus 中包含：
  // - .el-form-item__label（文字本身）
  // - .el-form-item__label-wrap（包裹区域，很多“左侧空白”落点在这里）
  const FORM_LABEL_AREA_SELECTOR = '.el-form-item__label, .el-form-item__label-wrap'

  function isHTMLElement(v: unknown): v is HTMLElement {
    return v instanceof HTMLElement
  }

  document.addEventListener(
    'pointerdown',
    (ev) => {
      const target = ev.target
      if (!isHTMLElement(target)) return

      // 点击在任意 popper 内：不处理（避免点选下拉项时被 blur）
      if (target.closest(POPPER_SELECTOR)) return

      // 🔧 关键：Element Plus 的 form-item label 默认是 <label for="...">
      // 点击“左侧 label/空白”会把点击转发到控件，相当于点了控件 → 下拉会被打开。
      // 诉求：仅当点击控件本体时才打开下拉，因此需要对“label 点击”做统一拦截（仅对下拉类控件）。
      const labelEl = target.closest(FORM_LABEL_AREA_SELECTOR)
      if (labelEl) {
        const item = target.closest(FORM_ITEM_SELECTOR)
        if (item && item.querySelector(TRIGGER_SELECTOR)) {
          // 若当前有下拉类控件处于聚焦状态，先 blur 触发关闭
          const active = document.activeElement
          if (isHTMLElement(active) && active.closest(TRIGGER_SELECTOR)) {
            active.blur()
          }
          ev.preventDefault()
          ev.stopPropagation()
          return
        }
      }

      // 点击在触发器内部：不处理（交给组件自身处理开关/切换）
      if (target.closest(TRIGGER_SELECTOR)) return

      const active = document.activeElement
      if (!isHTMLElement(active)) return

      // 仅对“弹层输入类组件”的聚焦元素生效，避免影响普通输入框
      const isActiveInTrigger = !!active.closest(TRIGGER_SELECTOR)
      if (!isActiveInTrigger) return

      // 主动 blur：让 Element Plus 的关闭链路生效
      active.blur()
    },
    true
  )
}

